import { Children, cloneElement, isValidElement } from 'react'
import type { CSSProperties, ReactElement, ReactNode } from 'react'
import { useInView } from '../hooks/useInView'

interface RevealProps {
  children: ReactNode
  delay?: number
  className?: string
  style?: CSSProperties
}

/** Fades + lifts its content into view once, on scroll. */
export function Reveal({ children, delay = 0, className, style }: RevealProps) {
  const { ref, inView } = useInView()
  return (
    <div
      ref={ref}
      className={['reveal', inView ? 'is-visible' : '', className].filter(Boolean).join(' ')}
      style={{ ...style, transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}

interface RevealStaggerProps {
  children: ReactNode
  step?: number
  className?: string
  style?: CSSProperties
}

/**
 * Container whose direct children reveal one after another. The container keeps
 * the layout (grid/flex via className/style); each child gets the reveal class
 * and an incremental transition-delay.
 */
export function RevealStagger({ children, step = 110, className, style }: RevealStaggerProps) {
  const { ref, inView } = useInView({ threshold: 0.08 })
  return (
    <div ref={ref} className={className} style={style}>
      {Children.toArray(children).map((child, i) => {
        if (!isValidElement(child)) return child
        const props = child.props as { className?: string; style?: CSSProperties }
        return cloneElement(child as ReactElement<{ className?: string; style?: CSSProperties }>, {
          className: ['reveal', inView ? 'is-visible' : '', props.className].filter(Boolean).join(' '),
          style: { ...props.style, transitionDelay: `${i * step}ms` },
        })
      })}
    </div>
  )
}
